"use client";
import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

type FormData = {
  name: string;
  company: string;
  email: string;
  phone: string;
  type: string;
  message: string;
};

type FormErrors = Partial<Record<keyof FormData | 'attachment', string>>;

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    type: '',
    message: ''
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email address';
    if (!form.phone.trim()) newErrors.phone = 'Phone is required';
    if (!form.type) newErrors.type = 'Please select an option';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    if (attachment && attachment.size > 10 * 1024 * 1024) {
      newErrors.attachment = 'File size must be under 10MB';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name as keyof FormData]) {
      setErrors({ ...errors, [e.target.name]: undefined });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAttachment(file);
    if (errors.attachment) setErrors({ ...errors, attachment: undefined });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError('');

    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('company', form.company);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('type', form.type);
      formData.append('message', form.message);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const res = await fetch('/api/contact', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        setSubmitError(data.error || 'Failed to send message. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
  {
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c2ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>,

    label: 'Address',
    value: 'Office No: 202, 2nd Floor, Building No: 08, Street No: 840, Zone 24, Al Muntazah, Doha, Qatar'
  },
  {
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c2ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.7h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.3a16 16 0 0 0 5.79 5.79l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>,

    label: 'Phone',
    value: '+974 5001 8878',
    href: 'tel:+97450018878'
  },
  {
    icon:
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c2ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>,

    label: 'Email',
    value: 'info@fornaxglob.com',
    href: 'mailto:info@fornaxglob.com'
  }];


  return (
    <section id="contact" ref={ref} className="relative py-24 overflow-hidden" style={{ background: '#020d1f' }}>
      <div className="dot-grid absolute inset-0 opacity-20 pointer-events-none" />
      <div
        className="glow-orb w-[500px] h-[500px] bottom-0 left-0 opacity-20"
        style={{ background: 'rgba(26,86,176,0.4)' }} />
      

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <p className="eyebrow mb-4">GET IN TOUCH</p>
          <h2
            className="text-3xl lg:text-5xl font-extrabold text-fg"
            style={{ fontFamily: 'Syne, sans-serif' }}>
            
            Let's Build Your{' '}
            <span className="gradient-text">Workforce Together</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Photo + Contact Info */}
          <div className="reveal-hidden stagger-1">
            <div className="relative rounded-2xl overflow-hidden mb-8" style={{ height: '300px' }}>
              <AppImage
                src="https://images.unsplash.com/photo-1701937756351-8552991a5bfe"
                alt="Doha Qatar skyline at night with modern skyscrapers reflecting on water"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(135deg, rgba(26,86,176,0.5) 0%, rgba(0,194,255,0.2) 100%)' }} />
              
              <div className="absolute bottom-6 left-6">
                <div
                  className="glass px-5 py-3 rounded-xl"
                  style={{ border: '1px solid rgba(0,194,255,0.3)' }}>
                  
                  <p className="text-fg font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>
                    📍 Doha, Qatar
                  </p>
                  <p className="text-muted text-xs">Al Muntazah, Zone 24</p>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-5">
              {contactInfo.map((info) =>
              <div
                key={info.label}
                className="flex items-start gap-4 p-4 rounded-xl transition-all duration-300"
                style={{
                  background: 'rgba(10,36,80,0.4)',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}>
                
                  <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(0,194,255,0.1)', border: '1px solid rgba(0,194,255,0.2)' }}>
                  
                    {info.icon}
                  </div>
                  <div>
                    <p className="text-muted text-xs uppercase tracking-[1px] font-bold mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {info.label}
                    </p>
                    {info.href ?
                  <a href={info.href} className="text-fg text-sm hover:text-cyan transition-colors duration-200" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {info.value}
                      </a> :

                  <p className="text-fg text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
                        {info.value}
                      </p>
                  }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Form */}
          <div className="reveal-hidden stagger-2">
            {submitted ?
            <div
              className="success-pop flex flex-col items-center justify-center text-center p-12 rounded-2xl"
              style={{ background: 'rgba(10,36,80,0.6)', border: '1px solid rgba(0,194,255,0.3)', minHeight: '400px' }}>
              
                <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6 text-4xl"
                style={{ background: 'linear-gradient(135deg, #00c2ff, #1a56b0)' }}>
                
                  ✓
                </div>
                <h3 className="text-fg text-2xl font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Message Sent!
                </h3>
                <p className="text-muted text-base" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
                  Thank you for reaching out. Our team will respond within 24 hours.
                </p>
              </div> :

            <form
              onSubmit={handleSubmit}
              className="p-8 rounded-2xl space-y-5"
              style={{ background: 'rgba(10,36,80,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
              noValidate>
              
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Full Name *
                    </label>
                    <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Ahmed Al-Rashidi"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm"
                    aria-required="true" />
                  
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Company
                    </label>
                    <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Company Name"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm" />
                  
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Email *
                    </label>
                    <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="ahmed@company.com"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm"
                    aria-required="true" />
                  
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Phone *
                    </label>
                    <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+974 XXXX XXXX"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm"
                    aria-required="true" />
                  
                    {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    I am a *
                  </label>
                  <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="form-input w-full px-4 py-3 rounded-xl text-sm"
                  aria-required="true">
                  
                    <option value="">Select an option...</option>
                    <option value="business">Business / Employer</option>
                    <option value="jobseeker">Job Seeker</option>
                    <option value="partner">Potential Partner</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.type && <p className="text-red-400 text-xs mt-1">{errors.type}</p>}
                </div>

                {/* Attachment Upload */}
                <div>
                  <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Attach CV / File <span className="normal-case font-normal opacity-60">(optional, max 10MB)</span>
                  </label>
                  <label
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm cursor-pointer transition-all duration-200"
                    style={{
                      background: 'rgba(0,194,255,0.05)',
                      border: '1px dashed rgba(0,194,255,0.3)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(0,194,255,0.6)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(0,194,255,0.3)')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00c2ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span className="text-muted text-sm flex-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {attachment ? attachment.name : 'Click to upload CV or attachment'}
                    </span>
                    {attachment && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setAttachment(null); }}
                        className="text-muted hover:text-red-400 transition-colors text-xs ml-auto">
                        ✕
                      </button>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                  </label>
                  {errors.attachment && <p className="text-red-400 text-xs mt-1">{errors.attachment}</p>}
                </div>

                <div>
                  <label className="block text-muted text-xs uppercase tracking-[1px] font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Message *
                  </label>
                  <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your workforce requirements..."
                  rows={4}
                  className="form-input w-full px-4 py-3 rounded-xl text-sm resize-none"
                  aria-required="true" />
                
                  {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                </div>

                {submitError && (
                  <p className="text-red-400 text-sm text-center">{submitError}</p>
                )}

                <button
                type="submit"
                disabled={loading}
                className="btn-gradient w-full py-4 rounded-xl font-semibold text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                
                  <span>{loading ? 'Sending...' : 'Send Message →'}</span>
                </button>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

}